import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import { useToast } from '@/hooks/use-toast';
import {
  CheckCircle2,
  Package,
  FileText,
  Sparkles,
  Info,
  Unlink,
} from 'lucide-react';
import { applyCOGSMappingsToInvoices } from '@/lib/applyTrainedMappings';
import { convertVendorInvoicesToExpenses } from '@/lib/cogsInvoiceToExpense';

interface COGSVendorTrainingStep7Props {
  onNext: () => void;
  onPrevious: () => void;
  selectedVendors: string[];
  /** When provided, called on "Complete Section" to persist onboarding_progress and navigate (module 4 save) */
  onComplete?: () => void | Promise<void>;
}

interface TrainingStats {
  totalVendors: number;
  totalIngredients: number;
  totalInvoices: number;
  totalMappings: number;
  totalInventoryItems: number;
}

export const COGSVendorTrainingStep7 = ({ onNext, onPrevious, selectedVendors, onComplete }: COGSVendorTrainingStep7Props) => {
  const { toast } = useToast();
  const [stats, setStats] = useState<TrainingStats | null>(null);
  const [unmappedIngredients, setUnmappedIngredients] = useState<Array<{ id: string; name: string; category: string; standard_unit: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    if (selectedVendors && selectedVendors.length > 0) {
      calculateStats();
    } else {
      setLoading(false);
      setStats({
        totalVendors: 0,
        totalIngredients: 0,
        totalInvoices: 0,
        totalMappings: 0,
        totalInventoryItems: 0,
      });
    }
  }, [selectedVendors]);

  const calculateStats = async () => {
    try {
      setLoading(true);
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;

      const [vendorsResult, invoicesResult, mappingsResult, inventoryResult] = await Promise.all([
        supabase
          .from('vendor_training_progress')
          .select('vendor_id, status')
          .eq('company_id', companyId)
          .in('vendor_id', selectedVendors),
        supabase
          .from('vendor_invoice_training')
          .select('id', { count: 'exact', head: false })
          .eq('company_id', companyId)
          .in('vendor_id', selectedVendors)
          .eq('ocr_status', 'completed'),
        supabase
          .from('vendor_ingredient_mappings')
          .select('ingredient_id, vendor_id, vendor_product_description, recipe_unit, cost_per_recipe_unit, is_coupon, cluster_id, ingredients:ingredient_id (id, name, category)')
          .eq('company_id', companyId)
          .in('vendor_id', selectedVendors),
        supabase
          .from('ingredients')
          .select('id', { count: 'exact', head: false })
          .eq('company_id', companyId),
      ]);

      const vendors = vendorsResult.data || [];
      const invoices = invoicesResult.data || [];
      const allMappings = mappingsResult.data || [];
      const linkedMappings = allMappings.filter((m: any) => m.ingredient_id);
      const totalInventoryItems = inventoryResult.count || 0;

      const uniqueIngredients = new Set(linkedMappings.map((m: any) => m.ingredient_id).filter(Boolean));
      const groupKeys = new Set<string>();
      (allMappings as any[]).forEach((m) => {
        const vendorId = m.vendor_id || 'unknown';
        if (m.cluster_id) {
          groupKeys.add(`${vendorId}::cluster::${m.cluster_id}`);
        } else {
          const desc = (m.vendor_product_description || '').toLowerCase().trim();
          if (desc) {
            groupKeys.add(`${vendorId}::desc::${desc}`);
          }
        }
      });

      setStats({
        totalVendors: vendors.length,
        totalIngredients: uniqueIngredients.size,
        totalInvoices: invoices.length,
        totalMappings: groupKeys.size || allMappings.length,
        totalInventoryItems,
      });

      const mappedIngredientIds = uniqueIngredients;
      const mappedDescriptions = new Set(
        allMappings
          .map((m: any) => (m.vendor_product_description || '').toLowerCase().trim())
          .filter(Boolean)
      );
      const { data: allInventoryItems } = await supabase
        .from('ingredients')
        .select('id, name, category, standard_unit, cost_per_standard_unit')
        .eq('company_id', companyId);

      const mappedIngredientNames = new Set(
        (allInventoryItems || [])
          .filter((item: any) => mappedIngredientIds.has(item.id))
          .map((item: any) => (item.name || '').toLowerCase().trim())
          .filter(Boolean)
      );

      const isFoodCostOrPackaging = (category: string): boolean => {
        const lower = (category || '').toLowerCase().trim();
        return (
          lower === 'food cost' || lower === 'food' ||
          lower === 'beverage cost' || lower === 'beverage' || lower === 'drinks' ||
          lower === 'packaging'
        );
      };

      const unmapped = (allInventoryItems || [])
        .filter((item: any) => {
          if (mappedIngredientIds.has(item.id)) return false;
          const normalizedName = (item.name || '').toLowerCase().trim();
          if (!normalizedName) return true;
          if (mappedIngredientNames.has(normalizedName)) return false;
          if (mappedDescriptions.has(normalizedName)) return false;
          for (const desc of mappedDescriptions) {
            if (desc.includes(normalizedName) || normalizedName.includes(desc)) return false;
          }
          return true;
        })
        .filter((item: any) => isFoodCostOrPackaging(item.category || 'food cost'))
        .filter((item: any) => !item.cost_per_standard_unit || item.cost_per_standard_unit <= 0)
        .map((item: any) => ({
          id: item.id,
          name: item.name,
          category: item.category || 'food cost',
          standard_unit: item.standard_unit || 'each',
        }));
      setUnmappedIngredients(unmapped);

    } catch (error: any) {
      console.error('Error calculating stats:', error);
      toast({
        title: 'Error',
        description: 'Failed to calculate statistics',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    try {
      setCompleting(true);
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) throw new Error('Company ID not found');

      if (unmappedIngredients.length > 0) {
        toast({
          title: 'Manual costs required',
          description:
            'Some inventory items from Module 3 were not found in any receipts and have no recipe unit cost data. Please complete the previous "Fill Missing Inventory Costs" step or update these items in the Inventory module before completing training.',
          variant: 'destructive',
        });
        setCompleting(false);
        return;
      }

      if (!selectedVendors || selectedVendors.length === 0) {
        toast({
          title: 'Error',
          description: 'No vendors selected. Please go back and select vendors.',
          variant: 'destructive',
        });
        setCompleting(false);
        return;
      }

      const { error: progressError } = await supabase
        .from('vendor_training_progress')
        .update({ status: 'completed' })
        .eq('company_id', companyId)
        .in('vendor_id', selectedVendors);

      if (progressError) throw progressError;

      try {
        for (const vendorId of selectedVendors) {
          await applyCOGSMappingsToInvoices(companyId, vendorId);
        }
      } catch (mappingError) {
        console.warn('Non-critical: Could not apply mappings to invoices:', mappingError);
      }

      let totalConverted = 0;
      try {
        for (const vendorId of selectedVendors) {
          const result = await convertVendorInvoicesToExpenses(companyId, vendorId);
          totalConverted += result.converted;
          console.log(`Converted ${result.converted} invoices to expenses for vendor ${vendorId}`);
        }
      } catch (conversionError) {
        console.warn('Non-critical: Could not convert all invoices to expenses:', conversionError);
      }

      toast({
        title: 'Success',
        description: `Training completed successfully! ${totalConverted > 0 ? `${totalConverted} invoice(s) added to expenses. ` : ''}Mappings will be applied to future invoices.`,
      });

      if (onComplete) {
        await onComplete();
      } else {
        await new Promise(resolve => setTimeout(resolve, 100));
        onNext();
      }
    } catch (error: any) {
      console.error('Error completing training:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to complete training',
        variant: 'destructive',
      });
      setCompleting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 py-8">
        <div className="space-y-2">
          <Skeleton className="h-6 w-96" />
          <Skeleton className="h-4 w-full max-w-2xl" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-5 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="space-y-6">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            No training data found. Please complete the previous steps first.
          </AlertDescription>
        </Alert>
        <div className="flex justify-between">
          <Button variant="outline" onClick={onPrevious}>
            Previous Step
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Training Complete</h3>
        <p className="text-sm text-muted-foreground">
          Review your training summary. Price trends are available in the dashboard after onboarding.
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Vendors Trained</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Package className="h-8 w-8 text-primary" />
                <div>
                  <div className="text-3xl font-bold">{stats.totalVendors}</div>
                  <div className="text-xs text-muted-foreground">vendors</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Invoices Processed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <FileText className="h-8 w-8 text-primary" />
                <div>
                  <div className="text-3xl font-bold">{stats.totalInvoices}</div>
                  <div className="text-xs text-muted-foreground">invoices</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Inventory Items Linked</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Sparkles className="h-8 w-8 text-primary" />
                <div>
                  <div className="text-3xl font-bold">{stats.totalIngredients}</div>
                  <div className="text-xs text-muted-foreground">of {stats.totalInventoryItems} in your list</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Items Not Linked</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Unlink className="h-8 w-8 text-amber-500" />
                <div>
                  <div className="text-3xl font-bold">{stats.totalInventoryItems - stats.totalIngredients}</div>
                  <div className="text-xs text-muted-foreground">of {stats.totalInventoryItems} in your list</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Training Complete</CardTitle>
          <CardDescription>
            All vendors have been successfully trained. The system is now ready to automatically process future invoices.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="text-sm">All vendors: Complete</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="text-sm">System mapped {stats?.totalIngredients || 0} ingredients</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="text-sm">Future invoices will auto-parse</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="text-sm">Price trends are being tracked</span>
          </div>
        </CardContent>
      </Card>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <div className="space-y-2">
            <p className="font-semibold">What's Next:</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Invoice totals are automatically recorded as COGS expenses in your financial data</li>
              <li>Future invoices from these vendors will automatically parse and extract line items</li>
              <li>Ingredient costs will update automatically based on invoice prices</li>
              <li>Price variance alerts will trigger when prices change significantly</li>
              <li>COGS calculations will use vendor-specific pricing from your training data</li>
              <li>Recipe costing will automatically use the mapped ingredient prices</li>
              <li>P&L and financial reports will include all COGS from vendor invoices</li>
            </ul>
          </div>
        </AlertDescription>
      </Alert>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onPrevious}>
          Previous Step
        </Button>
        <Button onClick={handleComplete} disabled={completing}>
          {completing ? 'Completing...' : 'Complete Section'}
        </Button>
      </div>
    </div>
  );
};
